/**
 * One sentence per withheld reason, in words a person can act on.
 *
 * A `Record` over the contract's `ProviderUnavailableReason` rather than a
 * switch, so a reason added to the wire fails typecheck here until it has copy.
 * Every surface that withholds a provider reads from this file: the picker
 * rail, the search header, and the Composer's disabled sentence. Two of those
 * once disagreed (the rail said "CMMD", the Composer said "Apex", for the same
 * thread), and before that the picker told a person that a Claude credential
 * still being delivered was a feature Forge had not built.
 *
 * Customer copy says Environment. `CONTEXT.md` keeps Runtime as the word the
 * API uses on the wire, and it is not a thing anyone buys.
 *
 * The module name predates the vocabulary this file now speaks; the reasons it
 * covers are the four in `CONTEXT.md` section "Provider availability", not the
 * deleted client-side ineligibility union.
 */
import type { ProviderUnavailableReason } from "@cmmd-forge/contracts";

import { PICKER_PROVIDER_DISPLAY_NAME } from "../components/chat/ModelListRow";
import type {
  NotUsableShellDetail,
  PickerRailProvider,
  ShellProviderAvailability,
} from "./providerAvailabilityJoin";

/**
 * Which of the Composer's two framings a sentence is written for.
 *
 * A picker row names the provider ("Connect Claude in Settings"); a locked
 * thread names the thread first ("This thread uses Claude, but..."), because
 * the person did not just pick that provider, an earlier turn on this thread
 * did. Tone is a parameter of one function rather than a second copy table, so
 * a reason added to the wire cannot get a locked sentence without an unlocked
 * one next to it.
 */
export type ProviderAvailabilityTone = "locked" | "unlocked";

type ProviderAvailabilityCopy = (providerLabel: string) => string;

const UNLOCKED_WITHHELD_PROVIDER_COPY: Record<ProviderUnavailableReason, ProviderAvailabilityCopy> =
  {
    // The only sentence that may say this, and only for a provider on the
    // roadmap list. Everything else names what is actually in the way.
    unbuilt: () => "Coming soon.",
    "not-offered": (providerLabel) => `Your organization has not enabled ${providerLabel}.`,
    "not-usable": (providerLabel) => `Connect ${providerLabel} in Settings to use these models.`,
    "not-on-this-environment": (providerLabel) =>
      `${providerLabel} is not installed on this Environment.`,
  };

const LOCKED_WITHHELD_PROVIDER_COPY: Record<ProviderUnavailableReason, ProviderAvailabilityCopy> = {
  unbuilt: () => "Coming soon.",
  "not-offered": (providerLabel) =>
    `This thread uses ${providerLabel}, which your organization no longer offers. Start a new thread with an available provider.`,
  "not-usable": (providerLabel) =>
    `This thread uses ${providerLabel}. Connect ${providerLabel} in Settings, then try again.`,
  "not-on-this-environment": (providerLabel) =>
    `This thread uses ${providerLabel}, which is not installed on this Environment.`,
};

/**
 * Not Usable is one reason with four sentences, not four reasons.
 *
 * The detail says which of them a person is looking at: no personal connection,
 * a credential in flight, a connection that has expired, or an Environment that
 * cannot take the credential at all. Telling someone to reconnect an account
 * that is already on its way is how a working setup gets pulled apart, and
 * telling them to wait for a delivery to a dead Environment (2026-09-06, a
 * boot-failed VM read "still being applied" for hours) is how they wait
 * forever.
 */
const UNLOCKED_NOT_USABLE_DETAIL_COPY: Record<NotUsableShellDetail, ProviderAvailabilityCopy> = {
  "no-connection": (providerLabel) => `Connect ${providerLabel} in Settings to use these models.`,
  "delivery-pending": (providerLabel) =>
    `${providerLabel} access is still being applied to this Environment.`,
  "reconnect-required": (providerLabel) =>
    `Reconnect ${providerLabel} in Settings to use these models.`,
  "environment-not-running": (providerLabel) =>
    `This Environment is not running, so ${providerLabel} cannot run on it. Restart the Environment, then try again.`,
};

const LOCKED_NOT_USABLE_DETAIL_COPY: Record<NotUsableShellDetail, ProviderAvailabilityCopy> = {
  "no-connection": (providerLabel) =>
    `This thread uses ${providerLabel}, but no personal ${providerLabel} connection is available. Connect ${providerLabel} in Settings, then try again.`,
  "delivery-pending": (providerLabel) =>
    `This thread uses ${providerLabel}, but its access is still being applied to this Environment.`,
  "reconnect-required": (providerLabel) =>
    `This thread uses ${providerLabel}, but its saved sign-in expired. Reconnect ${providerLabel} in Settings, then try again.`,
  "environment-not-running": (providerLabel) =>
    `This thread uses ${providerLabel}, but this Environment is not running. Restart the Environment, then try again.`,
};

/**
 * The Not on this Environment sub-case for a reading nobody actually
 * confirmed: a live probe that failed, timed out, or errored, or a
 * last-known catalog entry read while the Environment has no live connection
 * at all (forge#3588). Distinct from `notUsableDetailCopy` above because it
 * pairs with a different reason (`not-on-this-environment`, not
 * `not-usable`), so it is not part of that `Record`.
 */
const COULD_NOT_CHECK_COPY: Record<ProviderAvailabilityTone, ProviderAvailabilityCopy> = {
  unlocked: (providerLabel) => `${providerLabel} could not be checked on this Environment.`,
  locked: (providerLabel) =>
    `This thread uses ${providerLabel}, which could not be checked on this Environment.`,
};

/** The one sentence for a provider access lookup that failed, per tone. */
const PROVIDER_ACCESS_ERROR_COPY: Record<ProviderAvailabilityTone, string> = {
  unlocked: "Forge could not verify provider access. Try again shortly.",
  locked: "Forge couldn't verify provider access. Check your connection and try again.",
};

/**
 * The sentence for a lookup still in flight, per tone.
 *
 * Not a verdict, and the rail stays selectable while it shows: the picker must
 * not switch off the control for choosing a provider because it does not yet
 * know which one works. It exists because the model rows ARE greyed while the
 * answer is outstanding, and greying a row without a sentence is the thing this
 * program set out to delete.
 */
const PROVIDER_ACCESS_LOADING_COPY: Record<ProviderAvailabilityTone, string> = {
  unlocked: "Checking provider access.",
  locked: "Checking provider access before this thread can continue.",
};

/**
 * The sentence for one provider and one availability, or `null` when there is
 * nothing to say.
 *
 * `null` only for a usable provider. Every other state has something to say,
 * including the two where the Shell has no verdict yet: a row can carry a
 * sentence and still be selectable, which is what keeps "Checking provider
 * access." from repeating the failure that greyed out a working Codex on
 * `/new`.
 *
 * Labels come from the picker's display names rather than a second map, so a
 * provider is never called two different things in the same surface: apex
 * reads "CMMD" here exactly as it does on the rail row for the same thread.
 *
 * `tone` defaults to "unlocked", the picker rail's framing, so its call sites
 * did not need to change when the Composer's locked framing joined this file.
 */
export function describeProviderAvailability(
  provider: PickerRailProvider,
  availability: ShellProviderAvailability,
  tone: ProviderAvailabilityTone = "unlocked",
): string | null {
  if (availability.state === "usable") return null;
  if (availability.state === "unresolved") {
    return availability.cause === "error"
      ? PROVIDER_ACCESS_ERROR_COPY[tone]
      : PROVIDER_ACCESS_LOADING_COPY[tone];
  }
  const providerLabel = PICKER_PROVIDER_DISPLAY_NAME[provider];
  const withheldCopy =
    tone === "locked" ? LOCKED_WITHHELD_PROVIDER_COPY : UNLOCKED_WITHHELD_PROVIDER_COPY;
  const notUsableDetailCopy =
    tone === "locked" ? LOCKED_NOT_USABLE_DETAIL_COPY : UNLOCKED_NOT_USABLE_DETAIL_COPY;
  if (
    availability.reason === "not-on-this-environment" &&
    availability.detail === "could-not-check"
  ) {
    return COULD_NOT_CHECK_COPY[tone](providerLabel);
  }
  if (
    availability.reason === "not-usable" &&
    availability.detail !== undefined &&
    availability.detail !== "could-not-check"
  ) {
    return notUsableDetailCopy[availability.detail](providerLabel);
  }
  return withheldCopy[availability.reason](providerLabel);
}
